import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useInvertedCursor } from '../hooks/useInvertedCursor';
import { BackgroundBeams } from '../components/ui/background-beams';
import { BentoGrid, BentoGridItem } from '../components/ui/bento-grid';
import { InfiniteMovingCards } from '../components/ui/infinite-moving-cards';
import { SEO } from '../components/SEO';
import { InstagramReel } from '../components/InstagramReel';
import { RobotIcon, EnvelopeIcon, FilmIcon, BriefcaseIcon } from '../components/Icons';
import './Home.css';

const FadeInSection = ({ children, className = '', delay = '' }) => {
    const [ref, isVisible] = useIntersectionObserver({ triggerOnce: true, threshold: 0.1 });
    return (
        <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className} ${delay}`}>
            {children}
        </div>
    );
};

export const Home = () => {
    useInvertedCursor();
    
    useEffect(() => {
        // Add structured data for homepage
        const homepageSchema = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Mad Over Technology",
            "url": "https://madovertech.io",
            "potentialAction": {
                "@type": "SearchAction",
                "target": "https://madovertech.io/?s={search_term_string}",
                "query-input": "required name=search_term_string"
            }
        };

        let schemaScript = document.querySelector('script[data-schema="homepage"]');
        if (!schemaScript) {
            schemaScript = document.createElement('script');
            schemaScript.setAttribute('type', 'application/ld+json');
            schemaScript.setAttribute('data-schema', 'homepage');
            document.head.appendChild(schemaScript);
        }
        schemaScript.textContent = JSON.stringify(homepageSchema);
    }, []);

    return (
        <>
            <SEO 
                title="Mad Over Technology | Interactive Technology Solutions Provider"
                description="Experiential tech, AI content, and gamified ideas designed to make your brand unforgettable. AI Photo Booth, Field Force Management (NexaSync), and interactive tech solutions for events and exhibitions in Delhi, Mumbai, Bangalore."
                keywords="AI Photo Booth, Field Force Management, NexaSync, Interactive Technology, Experiential Marketing, AI Solutions, Event Technology, Brand Activation, Delhi, Mumbai, Bangalore, India"
                url="/"
            />
            <main className="home-page">
            {/* Hero Section */}
            <section className="hero-section relative overflow-hidden flex items-center">
                <BackgroundBeams className="z-0" />
                <div className="container mx-auto px-4 relative z-10 text-center">
                    <FadeInSection>
                        <h1 className="hero-title text-6xl md:text-8xl font-bold mb-6 text-neutral-900">
                            Make Every Interaction Unforgettable
                        </h1>
                        <p className="hero-subtitle text-xl md:text-2xl text-neutral-600 mb-8 max-w-3xl mx-auto">
                            Transform every interaction into a memorable moment. We blend cutting-edge AI technology with creative gamification to create immersive brand experiences that drive real results.
                        </p>
                        <div className="flex gap-4 justify-center flex-wrap">
                            <Link to="/ai-photobooth" className="cta-button">
                                <svg className="demo-icon" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                                    <path d="M18 19H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h5c.55 0 1-.45 1-1s-.45-1-1-1H5c-1.11 0-2 .89-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-6c0-.55-.45-1-1-1s-1 .45-1 1v5c0 .55-.45 1-1 1zM14 4c0 .55.45 1 1 1h2.59l-9.13 9.13c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L19 6.41V9c0 .55.45 1 1 1s1-.45 1-1V4c0-.55-.45-1-1-1h-5c-.55 0-1 .45-1 1z"/>
                                </svg>
                                <span>AI Photo Booth</span>
                            </Link>
                            <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20your%20interactive%20tech%20solutions" target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-button">
                                <svg className="whatsapp-icon" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                                <span>WhatsApp Us</span>
                            </a>
                        </div>
                    </FadeInSection>
                </div>
            </section>

            {/* AI Solutions Section */}
            <section className="solutions-section py-20 bg-white">
                <div className="container mx-auto px-4">
                    <FadeInSection>
                        <h2 className="section-title">Unlock the Power of AI-Led Experiences</h2>
                        <p className="section-subtitle text-center text-lg text-neutral-600 mb-12 max-w-2xl mx-auto">
                            AI-led experiences revolutionize how we interact, engage, and grow offering smarter solutions, personalized interactions, and seamless automation.
                        </p>
                    </FadeInSection>
                    
                    <div className="solutions-grid">
                        <FadeInSection className="solution-card" delay="stagger-1">
                            <div className="solution-icon"><RobotIcon className="w-14 h-14" /></div>
                            <h3 className="solution-title">AI Photo Booth Experience</h3>
                            <p className="solution-desc">Transform user images in real-time with brand-specific aesthetics, attire, and backdrops sparking delight and virality.</p>
                            <div className="solution-tags">
                                <span>Events</span>
                                <span>Exhibitions</span>
                                <span>Retail Activations</span>
                            </div>
                            <Link to="/ai-photobooth" className="solution-link">Learn more →</Link>
                        </FadeInSection>

                        <FadeInSection className="solution-card" delay="stagger-2">
                            <div className="solution-icon"><EnvelopeIcon className="w-14 h-14" /></div>
                            <h3 className="solution-title">AI Personalized Invites</h3>
                            <p className="solution-desc">Send unforgettable invites using voice cloning, lip-sync, and even singing avatars.</p>
                            <div className="solution-tags">
                                <span>Video</span>
                                <span>Voice</span>
                                <span>Messaging</span>
                            </div>
                            <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20AI%20Personalized%20Invites" target="_blank" rel="noopener noreferrer" className="solution-link">Learn more →</a>
                        </FadeInSection>

                        <FadeInSection className="solution-card" delay="stagger-3">
                            <div className="solution-icon"><FilmIcon className="w-14 h-14" /></div>
                            <h3 className="solution-title">AI Video Booth & Face Swap</h3>
                            <p className="solution-desc">Let users step into roles from your campaign narrative perfect for storytelling and mass engagement.</p>
                            <div className="solution-tags">
                                <span>Brand films</span>
                                <span>Character promotions</span>
                                <span>Campaigns</span>
                            </div>
                            <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20AI%20Video%20Booth" target="_blank" rel="noopener noreferrer" className="solution-link">Learn more →</a>
                        </FadeInSection>

                        <FadeInSection className="solution-card" delay="stagger-4">
                            <div className="solution-icon"><BriefcaseIcon className="w-14 h-14" /></div>
                            <h3 className="solution-title">Field Force Management</h3>
                            <p className="solution-desc">Streamline your field operations with intelligent workforce management and real-time tracking.</p>
                            <div className="solution-tags">
                                <span>NexaSync</span>
                                <span>Workforce</span>
                                <span>Tracking</span>
                            </div>
                            <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer" className="solution-link">Learn more →</a>
                        </FadeInSection>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section py-20 bg-gradient-to-br from-neutral-50 to-white">
                <div className="container mx-auto px-4">
                    <FadeInSection>
                        <div className="stats-grid">
                            <div className="stat-item">
                                <div className="stat-number">9+</div>
                                <div className="stat-label">Years of Innovation</div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-number">500+</div>
                                <div className="stat-label">Projects Completed</div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-number">2M+</div>
                                <div className="stat-label">Organic Impressions</div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-number">100+</div>
                                <div className="stat-label">Happy Customers</div>
                            </div>
                        </div>
                    </FadeInSection>
                </div>
            </section>

            {/* Add-On Technology */}
            <section className="addon-section py-20 bg-white">
                <div className="container mx-auto px-4">
                    <FadeInSection>
                        <h2 className="section-title">Add-On Technology that Amplify Impact</h2>
                        <p className="section-subtitle text-center text-lg text-neutral-600 mb-12 max-w-2xl mx-auto">
                            Next Generation engagement tools to supercharge your brand experiences
                        </p>
                    </FadeInSection>
                    
                    <div className="addon-grid">
                        <FadeInSection className="addon-card" delay="stagger-1">
                            <h3 className="addon-title">AR Filters & Immersive VR</h3>
                            <p className="addon-desc">Engage users in real-world or virtual environments using augmented or virtual reality.</p>
                            <div className="addon-tags">Event zones, product reveals, brand storytelling</div>
                        </FadeInSection>

                        <FadeInSection className="addon-card" delay="stagger-2">
                            <h3 className="addon-title">Gamified Solutions</h3>
                            <p className="addon-desc">Reward curiosity and interaction with games that inform, entertain, and drive longer user engagement.</p>
                            <div className="addon-tags">Booth attractions, campaign virality, lead collection</div>
                        </FadeInSection>

                        <FadeInSection className="addon-card" delay="stagger-3">
                            <h3 className="addon-title">QR-Based Solutions</h3>
                            <p className="addon-desc">Enable seamless access and participation using smart QR codes from check-ins and registrations to personalized journeys.</p>
                            <div className="addon-tags">Events, retail touchpoints, campaign tracking</div>
                        </FadeInSection>

                        <FadeInSection className="addon-card" delay="stagger-4">
                            <h3 className="addon-title">Microsite Design</h3>
                            <p className="addon-desc">Create central digital destinations for your campaign. From entries to galleries, let everything live in one branded, mobile-first hub.</p>
                            <div className="addon-tags">Online campaigns, contests, immersive journeys</div>
                        </FadeInSection>
                    </div>
                </div>
            </section>

            {/* Instagram Reel Section */}
            <section className="instagram-section py-20 bg-white">
                <div className="container mx-auto px-4">
                    <FadeInSection>
                        <h2 className="section-title">AI Content Creators in Action</h2>
                        <p className="section-subtitle text-center text-lg text-neutral-600 mb-12 max-w-2xl mx-auto">
                            See how we're transforming brand experiences with AI-powered content creation and interactive technology
                        </p>
                    </FadeInSection>
                    <div className="instagram-reels-grid">
                        <FadeInSection delay="stagger-1">
                            <InstagramReel url="https://www.instagram.com/reel/DHxKqDQtN_c/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==" />
                        </FadeInSection>
                        <FadeInSection delay="stagger-2">
                            <InstagramReel url="https://www.instagram.com/reel/DHuWZJMNTLy/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==" />
                        </FadeInSection>
                        <FadeInSection delay="stagger-3">
                            <InstagramReel url="https://www.instagram.com/reel/DHsd5jQt3yq/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==" />
                        </FadeInSection>
                    </div>
                </div>
            </section>

            {/* Trusted By Section */}
            <section className="py-20 bg-neutral-50 overflow-hidden">
                <FadeInSection>
                    <h2 className="section-title trusted-by-title">Trusted by Industry Leaders</h2>
                </FadeInSection>
                <InfiniteMovingCards
                    items={[
                        { logo: "/logos/pegafest.png", name: "PegaFest 25", quote: "PegaFest 25" },
                        { logo: "/logos/baliyatra.png", name: "Baliyatra", quote: "Baliyatra" },
                        { logo: "/logos/vidhan-sabha.png", name: "Vidhan Sabha", quote: "Vidhan Sabha" },
                        { logo: "/logos/ministry-power.svg", name: "Ministry of Power", quote: "Ministry of Power" },
                        { logo: "/logos/epfo.png", name: "EPFO", quote: "EPFO" },
                        { logo: "/logos/tvs.png", name: "TVS", quote: "TVS" },
                        { logo: "/logos/swiggy.png", name: "Swiggy Instamart", quote: "Swiggy Instamart" },
                        { logo: "/logos/ikea.png", name: "IKEA", quote: "IKEA" },
                        { logo: "/logos/queenfisher.png", name: "Queenfisher", quote: "Queenfisher" },
                    ]}
                    direction="right"
                    speed="slow"
                    className="bg-transparent"
                />
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <FadeInSection className="cta-content">
                    <h2 className="cta-title">Ready to Transform Your Brand Experience?</h2>
                    <p className="cta-subtitle">Let's connect to explore how we can help you achieve your goals through immersive, tech-driven solutions.</p>
                    <div className="cta-buttons">
                        <Link to="/ai-photobooth" className="cta-button">Explore AI Photo Booth</Link>
                        <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20your%20interactive%20tech%20solutions" target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-button">
                            <svg className="whatsapp-icon" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            <span>WhatsApp Us</span>
                        </a>
                    </div>
                </FadeInSection>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-links">
                    <Link to="/ai-photobooth">AI Photo Booth</Link>
                    <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer">NexaSync</a>
                    <Link to="/terms-of-service">Terms of Service</Link>
                    <Link to="/privacy-policy">Privacy Policy</Link>
                    <Link to="/refund-policy">Refund Policy</Link>
                    <a href="https://www.instagram.com/madovertechnology" target="_blank" rel="noopener noreferrer">Instagram</a>
                    <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20services" target="_blank" rel="noopener noreferrer">Contact</a>
                </div>
                <div className="footer-locations">
                    <span>DELHI | BENGALORE | MUMBAI</span>
                </div>
                <div className="footer-contact">
                    <a href="mailto:hello@madovertech.io" className="footer-email">hello@madovertech.io</a>
                    <span className="footer-separator">|</span>
                    <a href="tel:+919711689861" className="footer-phone">+91 9711689861</a>
                </div>
                <p className="footer-copyright">© 2025 Mad Over Technology. All rights reserved.</p>
            </footer>
            </main>
        </>
    );
};
